import { AlertCircle, Calendar, CheckCircle, Coins, CreditCard, DollarSign, Landmark, Plus, Smartphone, Wallet, X } from "lucide-react";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Actual.css";

const ACTUAL_API = "https://salathai.co.tz/mrms/Actual.php";

export default function Actual() {
    const [balances, setBalances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [todayBalance, setTodayBalance] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        cash_amount: "",
        mpesa_amount: "",
        crdb_bank_amount: "",
        selecom_amount: "",
        mix_by_yas_amount: ""
    });

    // Payment methods configuration
    const paymentMethods = [
        { key: "cash_amount", label: "CASH", icon: Coins, color: "#10b981", bgColor: "#d1fae5" },
        { key: "mpesa_amount", label: "M-PESA", icon: Smartphone, color: "#3b82f6", bgColor: "#dbeafe" },
        { key: "crdb_bank_amount", label: "CRDB BANK", icon: Landmark, color: "#8b5cf6", bgColor: "#ede9fe" },
        { key: "selecom_amount", label: "SELECOM", icon: CreditCard, color: "#f59e0b", bgColor: "#fed7aa" },
        { key: "mix_by_yas_amount", label: "MIX BY YAS", icon: Wallet, color: "#ef4444", bgColor: "#fee2e2" }
    ];

    const getToken = () => localStorage.getItem("token");

    useEffect(() => {
        const token = getToken();
        if (!token) {
            Swal.fire("Session Expired", "Please login again", "warning");
            return;
        }
        loadBalances();
        checkTodayBalance();
    }, []);

    // Filter balances for current month only
    const getCurrentMonthBalances = () => {
        return balances.filter(balance => {
            const balanceDate = new Date(balance.date);
            return balanceDate.getMonth() === currentMonth && 
                   balanceDate.getFullYear() === currentYear;
        });
    };

    const loadBalances = async () => {
        const token = getToken();
        if (!token) return;

        setLoading(true);
        try {
            const url = `${ACTUAL_API}?token=${token}`;
            const res = await fetch(url);
            const text = await res.text();
            
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}: ${text}`);
            }
            
            const data = JSON.parse(text);
            if (Array.isArray(data)) {
                setBalances(data);
            } else if (data.error) {
                Swal.fire("Error", data.error, "error");
            }
        } catch (err) {
            console.error("Load Balances Error:", err);
            Swal.fire("Error", "Cannot connect to server", "error");
        } finally {
            setLoading(false);
        }
    };

    const checkTodayBalance = async () => {
        const token = getToken();
        if (!token) return;

        try {
            const today = new Date().toISOString().split('T')[0];
            const url = `${ACTUAL_API}?action=check&date=${today}&token=${token}`;
            const res = await fetch(url);
            const data = await res.json();
            
            if (data.exists) {
                setTodayBalance(data.data);
            } else {
                setTodayBalance(null);
            }
        } catch (err) {
            console.error("Check Today Balance Error:", err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = getToken();
        
        if (!token) {
            Swal.fire("Error", "Authentication required", "error");
            return;
        }

        const hasAmount = Object.keys(formData).some(key => 
            key !== "date" && parseFloat(formData[key]) > 0
        );
        
        if (!hasAmount) {
            Swal.fire("Error", "Please enter at least one payment method amount", "error");
            return;
        }

        try {
            const submitData = {
                date: formData.date,
                cash_amount: parseFloat(formData.cash_amount) || 0,
                mpesa_amount: parseFloat(formData.mpesa_amount) || 0,
                crdb_bank_amount: parseFloat(formData.crdb_bank_amount) || 0,
                selecom_amount: parseFloat(formData.selecom_amount) || 0,
                mix_by_yas_amount: parseFloat(formData.mix_by_yas_amount) || 0
            };

            const res = await fetch(`${ACTUAL_API}?token=${token}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(submitData),
            });

            const data = await res.json();

            if (data.error) {
                if (res.status === 409) {
                    Swal.fire("Already Recorded", `Balance already recorded for ${formData.date}`, "warning");
                } else {
                    Swal.fire("Error", data.error, "error");
                }
            } else {
                Swal.fire("Success", data.message || "Balance recorded successfully", "success");
                setShowForm(false);
                setFormData({
                    date: new Date().toISOString().split('T')[0],
                    cash_amount: "",
                    mpesa_amount: "",
                    crdb_bank_amount: "",
                    selecom_amount: "",
                    mix_by_yas_amount: ""
                });
                loadBalances();
                checkTodayBalance();
            }
        } catch (err) {
            console.error("Submit Error:", err);
            Swal.fire("Error", "Failed to submit balance", "error");
        }
    };

    const formatCurrency = (amount) => 
        `TZS ${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const calculateTotal = (balance) => {
        return (balance.cash_amount || 0) + (balance.mpesa_amount || 0) + 
               (balance.crdb_bank_amount || 0) + (balance.selecom_amount || 0) + 
               (balance.mix_by_yas_amount || 0);
    };

    // Calculate stats for current month only
    const getCurrentMonthStats = () => {
        const currentMonthBalances = getCurrentMonthBalances();
        const totalBalance = currentMonthBalances.reduce((sum, b) => sum + (b.total_amount || calculateTotal(b)), 0);
        const avgDailyBalance = currentMonthBalances.length > 0 ? totalBalance / currentMonthBalances.length : 0;
        const methodTotals = {
            cash: currentMonthBalances.reduce((sum, b) => sum + (b.cash_amount || 0), 0),
            mpesa: currentMonthBalances.reduce((sum, b) => sum + (b.mpesa_amount || 0), 0),
            crdb: currentMonthBalances.reduce((sum, b) => sum + (b.crdb_bank_amount || 0), 0),
            selecom: currentMonthBalances.reduce((sum, b) => sum + (b.selecom_amount || 0), 0),
            mix: currentMonthBalances.reduce((sum, b) => sum + (b.mix_by_yas_amount || 0), 0)
        };
        return { 
            totalBalance, 
            avgDailyBalance, 
            methodTotals, 
            totalDays: currentMonthBalances.length,
            monthName: new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long' })
        };
    };

    const stats = getCurrentMonthStats();
    const currentMonthBalances = getCurrentMonthBalances();

    if (loading) {
        return (
            <div className="actual-loader-container">
                <div className="actual-spinner"></div>
                <p>Loading daily balances...</p>
            </div>
        );
    }

    const calculateFormTotal = () => {
        return (parseFloat(formData.cash_amount) || 0) +
               (parseFloat(formData.mpesa_amount) || 0) +
               (parseFloat(formData.crdb_bank_amount) || 0) +
               (parseFloat(formData.selecom_amount) || 0) +
               (parseFloat(formData.mix_by_yas_amount) || 0);
    };

    // Month navigation handlers
    const goToPreviousMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const goToNextMonth = () => {
        const now = new Date();
        if (currentYear === now.getFullYear() && currentMonth === now.getMonth()) {
            return;
        }
        
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const goToCurrentMonth = () => {
        setCurrentMonth(new Date().getMonth());
        setCurrentYear(new Date().getFullYear());
    };

    const getMonthYearDisplay = () => {
        return `${stats.monthName} ${currentYear}`;
    };

    const isCurrentMonth = () => {
        const now = new Date();
        return currentMonth === now.getMonth() && currentYear === now.getFullYear();
    };

    return (
        <div className="actual-container">
            {/* Month Navigation */}
            <div className="actual-month-navigation">
                <button onClick={goToPreviousMonth} className="actual-nav-btn">
                    ← Previous Month
                </button>
                <div className="actual-month-title">
                    <Calendar size={20} />
                    <h2>{getMonthYearDisplay()}</h2>
                </div>
                <button 
                    onClick={goToNextMonth} 
                    className={`actual-nav-btn ${isCurrentMonth() ? 'actual-nav-disabled' : ''}`}
                    disabled={isCurrentMonth()}
                >
                    Next Month →
                </button>
                <button onClick={goToCurrentMonth} className="actual-nav-btn actual-current-btn">
                    Current Month
                </button>
            </div>

            {/* Stats Cards - Current Month Only */}
            <div className="actual-stats-grid">
                <div className="actual-stat-card">
                    <div className="actual-stat-icon total-icon">
                        <DollarSign size={24} />
                    </div>
                    <div className="actual-stat-info">
                        <span className="actual-stat-label">Total Balance ({stats.monthName})</span>
                        <span className="actual-stat-value">{formatCurrency(stats.totalBalance)}</span>
                        <span className="actual-stat-sub">Across {stats.totalDays} days</span>
                    </div>
                </div>
                <div className="actual-stat-card">
                    <div className="actual-stat-icon avg-icon">
                        <Calendar size={24} />
                    </div>
                    <div className="actual-stat-info">
                        <span className="actual-stat-label">Average Daily Balance</span>
                        <span className="actual-stat-value">{formatCurrency(stats.avgDailyBalance)}</span>
                        <span className="actual-stat-sub">Per day average</span>
                    </div>
                </div>
            </div>

            {/* Payment Method Totals - Current Month Only */}
            <div className="actual-methods-section">
                <h3 className="actual-section-title">Payment Method Totals ({stats.monthName})</h3>
                <div className="actual-methods-grid">
                    <div className="actual-method-card cash">
                        <Coins size={18} />
                        <span>CASH</span>
                        <strong>{formatCurrency(stats.methodTotals.cash)}</strong>
                    </div>
                    <div className="actual-method-card mpesa">
                        <Smartphone size={18} />
                        <span>M-PESA</span>
                        <strong>{formatCurrency(stats.methodTotals.mpesa)}</strong>
                    </div>
                    <div className="actual-method-card crdb">
                        <Landmark size={18} />
                        <span>CRDB BANK</span>
                        <strong>{formatCurrency(stats.methodTotals.crdb)}</strong>
                    </div>
                    <div className="actual-method-card selecom">
                        <CreditCard size={18} />
                        <span>SELECOM</span>
                        <strong>{formatCurrency(stats.methodTotals.selecom)}</strong>
                    </div>
                    <div className="actual-method-card mix">
                        <Wallet size={18} />
                        <span>MIX BY YAS</span>
                        <strong>{formatCurrency(stats.methodTotals.mix)}</strong>
                    </div>
                </div>
            </div>

            {/* Today's Status */}
            {todayBalance && (
                <div className="actual-today-card">
                    <div className="actual-today-header">
                        <CheckCircle size={18} />
                        <span>Today's Balance Recorded</span>
                    </div>
                    <div className="actual-today-content">
                        {paymentMethods.map(method => {
                            const amount = todayBalance[method.key] || 0;
                            if (amount > 0) {
                                return (
                                    <div key={method.key} className="actual-today-method">
                                        <method.icon size={14} />
                                        <span>{method.label}:</span>
                                        <strong>{formatCurrency(amount)}</strong>
                                    </div>
                                );
                            }
                            return null;
                        })}
                        <div className="actual-today-total">
                            <span>Total:</span>
                            <strong>{formatCurrency(todayBalance.total_amount || calculateTotal(todayBalance))}</strong>
                        </div>
                    </div>
                </div>
            )}

            {/* History Table - Current Month Only */}
            <div className="actual-history-section">
                <h3 className="actual-section-title">Balance History ({stats.monthName})</h3>
                <div className="actual-table-container">
                    <table className="actual-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>CASH</th>
                                <th>M-PESA</th>
                                <th>CRDB</th>
                                <th>SELECOM</th>
                                <th>MIX BY YAS</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentMonthBalances.length > 0 ? (
                                currentMonthBalances.map((balance) => (
                                    <tr key={balance.id}>
                                        <td className="actual-date-cell">{formatDate(balance.date)}</td>
                                        <td>{formatCurrency(balance.cash_amount)}</td>
                                        <td>{formatCurrency(balance.mpesa_amount)}</td>
                                        <td>{formatCurrency(balance.crdb_bank_amount)}</td>
                                        <td>{formatCurrency(balance.selecom_amount)}</td>
                                        <td>{formatCurrency(balance.mix_by_yas_amount)}</td>
                                        <td className="actual-total-cell">{formatCurrency(balance.total_amount || calculateTotal(balance))}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="actual-empty-cell">
                                        <AlertCircle size={40} />
                                        <p>No balance records found for {stats.monthName}</p>
                                        <span>Click the + button to add a balance record</span>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Static Floating + Button */}
            <button 
                className="actual-fab-button" 
                onClick={() => setShowForm(true)}
                aria-label="Add balance"
            >
                <Plus size={28} />
            </button>

            {/* Popup Form Modal */}
            {showForm && (
                <div className="actual-modal-overlay" onClick={() => setShowForm(false)}>
                    <div className="actual-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="actual-modal-header">
                            <div className="actual-modal-title">
                                <Wallet size={22} />
                                <h3>Record Balance</h3>
                            </div>
                            <button className="actual-modal-close" onClick={() => setShowForm(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="actual-form">
                            <div className="actual-form-group full-width">
                                <label className="actual-form-label">
                                    <Calendar size={16} />
                                    Select Date
                                </label>
                                <input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    className="actual-form-input"
                                    required
                                    max={new Date().toISOString().split('T')[0]}
                                />
                            </div>
                            
                            <div className="actual-form-fields">
                                {paymentMethods.map((method) => (
                                    <div key={method.key} className="actual-form-group">
                                        <label className="actual-form-label">
                                            <method.icon size={16} style={{ color: method.color }} />
                                            {method.label}
                                        </label>
                                        <input
                                            type="number"
                                            step="1000"
                                            placeholder="0.00"
                                            value={formData[method.key]}
                                            onChange={(e) => setFormData({ ...formData, [method.key]: e.target.value })}
                                            className="actual-form-input"
                                        />
                                    </div>
                                ))}
                            </div>
                            
                            <div className="actual-form-total">
                                <span>Total Balance:</span>
                                <strong>{formatCurrency(calculateFormTotal())}</strong>
                            </div>
                            
                            <div className="actual-form-actions">
                                <button type="button" className="actual-btn-cancel" onClick={() => setShowForm(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="actual-btn-submit">
                                    Save Balance
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}